import { useState } from "react";
import { Modal, Tag } from "antd";
import { HolderOutlined } from "@ant-design/icons";
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  pointerWithin,
  rectIntersection,
  useDroppable,
  useSensor,
  useSensors,
  type CollisionDetection,
  type DragEndEvent,
  type DragStartEvent,
  type Over,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { ActivityType } from "../../../../types";
import type { FormActivityUI } from "../utils";
import { resolveActivityType } from "../utils";
import {
  ACTIVITY_SECTION_ORDER,
  buildReorderGroups,
  flattenReorderGroups,
  moveReorderRow,
  type ReorderGroups,
  type ReorderRow,
} from "../activityReorder";
import "./activityReorderPanel.scss";

interface ActivityReorderPanelProps {
  open: boolean;
  activities: FormActivityUI[];
  /** Receives the full reordered `lesson.activities` array. */
  onApply: (activities: FormActivityUI[]) => void;
  onClose: () => void;
}

const isSectionId = (id: string): id is ActivityType =>
  (ACTIVITY_SECTION_ORDER as readonly string[]).includes(id);

/**
 * A row must win over the section box that contains it. Each section is also a
 * droppable so that an empty one can still receive a drop, but that box is far
 * larger than the rows inside it, so a plain corner/centre strategy resolves to
 * the section even when the pointer is right on top of a row — which silently
 * turned every same-section reorder into "append where it already was".
 * The section is therefore only used when no row is under the pointer.
 */
export const preferRowOverSection = <T extends { id: string | number }>(
  candidates: T[],
): T[] => {
  const row = candidates.find((hit) => !isSectionId(String(hit.id)));
  return row ? [row] : candidates;
};

const rowsBeforeSections: CollisionDetection = (args) => {
  const hits = pointerWithin(args);
  return preferRowOverSection(hits.length > 0 ? hits : rectIntersection(args));
};

/**
 * Where a drop should land, derived from our own `groups` model and nothing
 * else. `over.id` is either a section id or a row key; resolving the section and
 * index ourselves keeps this independent of @dnd-kit's internal sortable
 * metadata, which is not guaranteed to be present on every `over`.
 */
const resolveDropTarget = (
  groups: ReorderGroups,
  over: Over,
): { toType: ActivityType; toIndex: number } | null => {
  const overId = String(over.id);

  // Dropped on the section itself (its empty area) — append to the end.
  if (isSectionId(overId)) {
    return { toType: overId, toIndex: groups[overId].length };
  }

  // Dropped on a row — take that row's place.
  for (const type of ACTIVITY_SECTION_ORDER) {
    const index = groups[type].findIndex((row) => row.key === overId);
    if (index !== -1) return { toType: type, toIndex: index };
  }
  return null;
};

interface RowContentProps {
  row: ReorderRow;
  position?: number;
  dragHandle?: React.ReactNode;
}

const RowContent: React.FC<RowContentProps> = ({
  row,
  position,
  dragHandle,
}) => (
  <>
    {dragHandle}
    {position !== undefined && (
      <span className="reorderRowPosition">{position}.</span>
    )}
    <span className="reorderRowTitle">
      {row.activity.title?.trim() || "Actividad sin título"}
    </span>
    <Tag className="reorderRowDuration" bordered={false}>
      {row.activity.duration || 0} min
    </Tag>
  </>
);

interface SortableRowProps {
  row: ReorderRow;
  position: number;
}

const SortableRow: React.FC<SortableRowProps> = ({ row, position }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: row.key });

  return (
    <li
      ref={setNodeRef}
      className={`reorderRow${isDragging ? " reorderRowDragging" : ""}`}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
      }}
    >
      <RowContent
        row={row}
        position={position}
        dragHandle={
          <button
            type="button"
            className="reorderDragHandle"
            aria-label={`Mover ${
              row.activity.title?.trim() || "actividad sin título"
            } (arrastra o usa las flechas del teclado)`}
            {...attributes}
            {...listeners}
          >
            <HolderOutlined />
          </button>
        }
      />
    </li>
  );
};

interface ReorderSectionProps {
  type: ActivityType;
  rows: ReorderRow[];
}

const ReorderSection: React.FC<ReorderSectionProps> = ({ type, rows }) => {
  const { setNodeRef, isOver } = useDroppable({ id: type });

  return (
    <section className="reorderSection">
      <h4 className="reorderSectionTitle">
        {resolveActivityType(type)}
        <span className="reorderSectionCount">{rows.length}</span>
      </h4>
      <SortableContext
        id={type}
        items={rows.map((row) => row.key)}
        strategy={verticalListSortingStrategy}
      >
        <ul
          ref={setNodeRef}
          className={`reorderSectionList${isOver ? " reorderSectionListOver" : ""}`}
          data-testid={`reorder-section-${type}`}
        >
          {rows.length === 0 ? (
            <li className="reorderSectionEmpty">Arrastra una actividad aquí</li>
          ) : (
            rows.map((row, index) => (
              <SortableRow key={row.key} row={row} position={index + 1} />
            ))
          )}
        </ul>
      </SortableContext>
    </section>
  );
};

/**
 * Cross-section reordering for the activities of a lesson. The lesson form edits
 * activities inside tabs, which cannot receive a drop, so the whole lesson is
 * mirrored here as three compact droppable lists. Changes are kept as a local
 * draft and only handed back on "Aplicar"; persistence stays with the form's
 * normal submit.
 */
const ActivityReorderPanel: React.FC<ActivityReorderPanelProps> = ({
  open,
  activities,
  onApply,
  onClose,
}) => {
  const [groups, setGroups] = useState<ReorderGroups>(() =>
    buildReorderGroups(activities),
  );
  const [activeKey, setActiveKey] = useState<string | null>(null);
  const [wasOpen, setWasOpen] = useState(open);

  // The draft is re-seeded on the closed → open transition only, so an edit
  // elsewhere in the form cannot wipe a reorder in progress.
  if (wasOpen !== open) {
    setWasOpen(open);
    if (open) setGroups(buildReorderGroups(activities));
  }

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragStart = (event: DragStartEvent) =>
    setActiveKey(String(event.active.id));

  // The move is applied once, on drop. Relocating the row during `onDragOver`
  // for a live preview means `onDragEnd` then resolves its target against an
  // already-modified model and applies a second, off-by-one move.
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveKey(null);
    if (!over) return;
    const rowKey = String(active.id);
    setGroups((prev) => {
      const target = resolveDropTarget(prev, over);
      return target ? moveReorderRow(prev, { rowKey, ...target }) : prev;
    });
  };

  const activeRow = activeKey
    ? ACTIVITY_SECTION_ORDER.flatMap((type) => groups[type]).find(
        (row) => row.key === activeKey,
      )
    : undefined;

  return (
    <Modal
      title="Reordenar actividades"
      open={open}
      onOk={() => {
        onApply(flattenReorderGroups(groups));
        onClose();
      }}
      onCancel={onClose}
      okText="Aplicar"
      cancelText="Cancelar"
      width={640}
      destroyOnClose
    >
      <p className="reorderHint">
        Arrastra una actividad para cambiar su posición o moverla a otra sección.
        Los cambios se guardan al guardar la lección.
      </p>
      <DndContext
        sensors={sensors}
        collisionDetection={rowsBeforeSections}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragCancel={() => setActiveKey(null)}
      >
        <div className="reorderSections">
          {ACTIVITY_SECTION_ORDER.map((type) => (
            <ReorderSection key={type} type={type} rows={groups[type]} />
          ))}
        </div>
        <DragOverlay>
          {activeRow ? (
            <ul className="reorderSectionList reorderOverlayList">
              <li className="reorderRow reorderRowOverlay">
                <RowContent
                  row={activeRow}
                  dragHandle={
                    <span className="reorderDragHandle">
                      <HolderOutlined />
                    </span>
                  }
                />
              </li>
            </ul>
          ) : null}
        </DragOverlay>
      </DndContext>
    </Modal>
  );
};

export default ActivityReorderPanel;
